/**
 * ================================================================
 * UI COMPONENT: NotificationItem
 * ================================================================
 *
 * @component        NotificationItem
 * @description      Individual notification item with read/unread states
 * @category         UI Component
 *
 * @props
 * - notification: Notification - Notification data object
 * - onMarkAsRead?: (id: string) => void - Callback when marking as read
 * - className?: string - Additional CSS classes
 *
 * @styling
 * - Base: Tailwind CSS utility classes with design system tokens
 * - States: Read/unread visual differentiation
 * - Responsive: Mobile-first approach
 *
 * @accessibility
 * - ARIA labels for read status
 * - Keyboard navigation support
 * - Screen reader friendly content
 *
 * @usage
 * ```tsx
 * <NotificationItem
 *   notification={notificationData}
 *   onMarkAsRead={handleMarkAsRead}
 * />
 * ```
 * ================================================================
 */

import { memo } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';

import { cn } from '@/lib/utils/utils';
import type { NotificationItemProps } from '@/types/notifications';

export const NotificationItem = memo(function NotificationItem({
  notification,
  onMarkAsRead,
  className,
  type,
}: NotificationItemProps) {
  // ================================================================
  // DERIVED STATE
  // ================================================================
  //
  // Compute display values from notification data
  // Handle date formatting and read status
  // ================================================================

  const isUnread = notification.status === 'UNREAD';
  const createdAt = new Date(notification.created_at);

  // Format date as dd-MM-yyyy
  const formattedDate = format(createdAt, 'dd-MM-yyyy');

  // Format time as hh:mm:ss a
  const formattedTime = format(createdAt, 'hh:mm:ss a');

  const isLong = notification.description.length > 30;
  const displayText = type === 'drawer'
    ? notification.description
    : notification.description.slice(0, 30) + (isLong ? '...' : '');

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={cn(
        // ================================================================
        // BASE STYLES - Using design system tokens
        // ================================================================
        //
        // Layout: Flex container with proper spacing
        // Background: Uses card background from design system
        // Borders: Subtle borders with hover effects
        // ================================================================
        'group relative flex w-full gap-3 p-4 transition-all duration-200',
        'cursor-pointer rounded-lg hover:bg-accent/50',

        // ================================================================
        // STATE-BASED STYLES - Using design system colors
        // ================================================================
        //
        // Unread: More prominent styling with primary color accent
        // Read: Muted styling to indicate processed status
        // ================================================================
        {
          'border-l-4 border-l-primary bg-primary/5': isUnread,
          'bg-card': !isUnread,
        },
        className,
        className,
      )}
      role="article"
      aria-label={`Notification: ${notification.title}`}
      tabIndex={0}
    >
      {/* ================================================================
           STATUS INDICATOR
           ================================================================
           
           Visual indicator for read/unread status using design system colors
           ================================================================ */}
      <div className="mt-2 flex-shrink-0">
        <div
          className={cn('h-2 w-2 rounded-full', {
            'bg-primary shadow-sm': isUnread,
            'bg-muted-foreground/40': !isUnread,
          })}
          aria-hidden="true"
        />
      </div>

      {/* ================================================================
           NOTIFICATION CONTENT
           ================================================================
           
           Main content area with title, description, and timestamp
           Using design system typography and colors
           ================================================================ */}
      <div className="min-w-0 flex-1">
        {/* Title */}
        <div className="flex items-start justify-between gap-2">
          <h4
            className={cn(
              'cursor-pointer text-sm font-medium leading-5 transition-colors',
              {
                'text-foreground': isUnread,
                'text-muted-foreground': !isUnread,
              }, // Apply color change on hover and focus
            )}
          >
            {notification.title}
          </h4>
        </div>

        {/* Description */}
        <p
          className={cn('mt-1 text-xs leading-4', {
            'text-foreground/80': isUnread,
            'text-muted-foreground': !isUnread,
          })}
        >
          {displayText}
        </p>

        {/* Timestamp and status */}
        {!isUnread && onMarkAsRead ? (
          <div className="mt-2 flex justify-between gap-2">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-primary" aria-hidden="true" />
              <time className="text-xs font-medium text-primary">{formattedTime}</time>
            </div>
            <div>
              <time className="pr-5 text-xs font-medium text-primary">{formattedDate}</time>
            </div>
          </div>
        ) : (
          <div className="mt-2 flex items-center gap-2">
            <Clock className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(createdAt, { addSuffix: true })}
            </span>
          </div>
        )}
      </div>

      {/* ================================================================
           ACCESSIBILITY ENHANCEMENTS
           ================================================================
           
           Screen reader content for better accessibility
           ================================================================ */}
      <span className="sr-only">
        {isUnread ? 'Unread notification' : 'Read notification'}
        {notification.read_at &&
          `, read ${formatDistanceToNow(new Date(notification.read_at), { addSuffix: true })}`}
        {notification.read_at &&
          `, read ${formatDistanceToNow(new Date(notification.read_at), { addSuffix: true })}`}
      </span>
    </motion.div>
  );
});

/**
 * ================================================================
 * END OF COMPONENT: NotificationItem
 * ================================================================
 *
 * @exports          NotificationItem - Individual notification component
 * @dependencies     React (memo), Framer Motion, date-fns, Lucide icons
 * @usedBy           NotificationsList and NotificationsPanel components
 *
 * @designSystem     Uses shadcn/ui design tokens and CSS custom properties
 * ================================================================
 */
